import {
   createFileRoute,
   notFound,
   Outlet,
   useMatches,
} from "@tanstack/react-router"
import { useEffect } from "react"
import { z } from "zod"
import { CACHE_FOREVER } from "@/api"
import { BottomNavigation } from "@/layout/components/bottom-navigation"
import { Main } from "@/layout/components/main"
import { PendingComponent } from "@/layout/components/pending-component"
import { Sidebar, SidebarContent } from "@/layout/components/sidebar"
import { useUnreadCount } from "@/order/message/read/queries"
import { SocketProvider } from "@/socket/provider"
import { trpc } from "@/trpc"
import { validator } from "@/validator"

export const Route = createFileRoute("/_authed/$workspaceId/_layout")({
   component: RouteComponent,
   validateSearch: validator(
      z.object({ attachmentId: z.optional(z.string()) }),
   ),
   beforeLoad: async (opts) => {
      const workspace = await opts.context.queryClient
         .ensureQueryData(
            trpc.workspace.one.queryOptions(
               { id: opts.params.workspaceId },
               { staleTime: CACHE_FOREVER },
            ),
         )
         .catch(() => {
            throw notFound()
         })

      if (!workspace) throw notFound()

      return {
         workspace,
      }
   },
   pendingComponent: () => (
      <>
         <Sidebar />
         <Main className="h-screen">
            <PendingComponent />
         </Main>
      </>
   ),
})

function RouteComponent() {
   const matches = useMatches()
   const isOnBoard = matches.some(
      (m) => m.routeId === "/_authed/$workspaceId/_layout/board",
   )
   const unreadCount = useUnreadCount()

   useEffect(() => {
      const faviconLink = document.querySelector(
         "link[rel='icon']",
      ) as HTMLLinkElement | null

      if (faviconLink) {
         faviconLink.href =
            unreadCount > 0 ? "/favicon_unread.ico" : "/favicon.ico"
      }
   }, [unreadCount])

   return (
      <SocketProvider>
         {isOnBoard ? null : (
            <Sidebar className="md:motion-preset-fade">
               <SidebarContent />
            </Sidebar>
         )}
         <Main innerClassName="md:motion-preset-fade">
            <Outlet />
         </Main>
         <BottomNavigation />
      </SocketProvider>
   )
}
